import { TypeOmit } from "@/components/common/const";
import { Input } from "@customer-tools-web/component-ui";
import { InputNumber } from "antd";
import { InputNumberProps } from "antd/lib/input-number";
import * as React from "react";

import { KEYBOARD } from "../const";
type ValueType = number | string | null;
type NumberInputProps = {
  field: string;
  value?: number;
  defaultVal?: number;
  length?: number;
  toThousand?: boolean;
  allowNegative?: boolean;
  onChange?: (value: number | string | undefined) => void;
  onBlur?: (value: number | undefined) => void;
  onEnterPress?: (value: number | undefined) => void;
} & TypeOmit<InputNumberProps, "onBlur">;

const numberReg = /^\-?((0|[1-9]\d*)(\.\d*)?)?$/;
const intergerReg = /^\-?(0|[1-9]\d*)?$/;

class NumberInput extends React.Component<NumberInputProps> {
  static MAX_LENGTH = 15;
  static defaultProps = {
    precision: 0,
    maxLength: NumberInput.MAX_LENGTH,
  };
  static isNumberFormat = (value?: ValueType) =>
    !!(value === 0 || value) &&
    (typeof value === "number" || numberReg.test(value));
  static isIntergerFormat = (value?: ValueType) =>
    !!(value === 0 || value) &&
    (typeof value === "number" || intergerReg.test(value));
  static transferToNumber = (value?: ValueType) =>
    NumberInput.isNumberFormat(value) ? parseFloat(value as string) : NaN;

  render() {
    const { field, defaultVal, precision } = this.props;

    return (
      <Input
        as={InputNumber}
        {...this.props}
        key={precision}
        precision={precision}
        maxLength={undefined}
        formatter={this.formatToDisplayString}
        parser={this.parseDisplayStringToNumber}
        defaultValue={defaultVal}
        onChange={this.onChange}
        onBlur={this.onBlur}
        onKeyDown={this.onKeyDown}
        data-test={`${field}`}
      />
    );
  }

  private formatToDisplayString = (value: string | number | undefined) => {
    const { toThousand, precision } = this.props;
    let val = `${value || value === 0 ? value : ""}`;
    if (toThousand) {
      val =
        val.indexOf(".") > 0
          ? val.replace(/\B(?=(\d{3})+\D)/g, ",")
          : val.replace(/\B(?=(\d{3})+$)/g, ",");
    }
    if (precision! > 0) {
      const reg = new RegExp(`(\\.\\d{${precision}})\\d*$`);
      val = val.replace(reg, "$1");
    }
    return val;
  };

  private parseDisplayStringToNumber = (value: string | undefined): any => {
    const { maxLength, precision, allowNegative } = this.props;

    if (value) {
      const val = value.replace(/(?:^(-?)0(?=\d))|,/g, "$1");
      const isLessThanMax =
        val.replace(/(?:^\-)|\./, "").length <= maxLength! + 1;
      const allowNumber = val.startsWith("-") ? allowNegative : true;
      if (allowNumber && isLessThanMax) {
        const isFloat = precision! > 0;
        if (
          (isFloat && NumberInput.isNumberFormat(val)) ||
          NumberInput.isIntergerFormat(val)
        ) {
          return val;
        }
      }
      if (this.props.value) {
        return this.props.value;
      }
    }
    return NaN;
  };

  private onChange = (value: number | string | undefined) => {
    if (this.props.onChange) {
      this.props.onChange(
        typeof value === "number" && isNaN(value) ? undefined : value
      );
    }
  };
  private onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (this.props.onBlur) {
      this.onOriginalEvent(event.target.value, this.props.onBlur);
    }
  };

  private onKeyDown = (event: any) => {
    if (this.props.onEnterPress && event.keyCode === KEYBOARD.enter) {
      this.onOriginalEvent(event.target.value, this.props.onEnterPress);
    }
  };

  private onOriginalEvent = (
    value: string,
    onFunc: (value: number | undefined) => void
  ) => {
    if (this.props.toThousand) {
      value = value.replace(/,/gi, "");
    }
    const valueNumber = NumberInput.transferToNumber(value);
    onFunc(isNaN(valueNumber) ? undefined : valueNumber);
  };
}

export { NumberInputProps, NumberInput };
export default NumberInput;
